import { __rest } from './../../external/tslib/tslib.es6.js';
import React, { useState, useRef, useEffect } from 'react';
import { CloseFill } from '@olist/design-system-icons';
import { Transition } from 'react-transition-group';
import { transitionStyles, ANIMATION_DURATION, wrapperTransitionStyles, transitionStylesMobile, CONTAINER_WIDTH } from './constants.js';
import { DrawerHeaderContainer, DrawerBodyContainer, DrawerWrapper, DrawerContainer, DrawerCloseWrapper } from './styles.js';
import Portal from '../../hooks/Portal.js';
import useIsLowerResolution from '../../hooks/useIsLowerResolution.js';
import { PORTAL_PREFIX } from '../utils/portalIdPrefix.js';
import { ButtonIcon } from '../ButtonIcon/ButtonIcon.js';
import '@olist/design-system-tokens';
import '../Alert/styles.js';
import '../Flex/Flex.js';
import '../Badge/Badge.js';
import '../BadgeNotification/styles.js';
import '../Text/Caption/Caption.js';
import '../Text/DetailedSubheading/DetailedSubheading.js';
import '../Text/OverlineLg/OverlineLg.js';
import '../Text/OverlineMd/OverlineMd.js';
import '../Text/ParagraphLg/ParagraphLg.js';
import '../Text/ParagraphMd/ParagraphMd.js';
import '../Text/SectionHeading/SectionHeading.js';
import '../Text/SectionSubheading/SectionSubheading.js';
import '../Text/Title/Title.js';
import '../Box/Box.js';
import '../Breadcrumb/styles.js';
import '../Dropdown/Dropdown.js';
import '../Link/styles.js';
import '../Button/Button.js';
import '../Card/Card.js';
import '../Checkbox/styles.js';
import '../Checkbox/utils/CheckboxIcon.js';
import '../Collapse/styles.js';
import '../CopyButton/styles.js';
import '../Tooltip/Tooltip.js';
import '@datepicker-react/hooks';
import '../DatePicker/context.js';
import '../DatePicker/styles.js';
import '../Input/Input.js';
import '../Input/InputBody.js';
import '../Input/InputPassword/styles.js';
import '../DescriptionItem/styles.js';
import '../Divider/Divider.js';
import './index.js';
import '../FileUploader/styles.js';
import '../FilterBadge/styles.js';
import '../Grid/Grid.js';
import '../InputSearch/InputSearch.js';
import '../InputVerificationCode/styles.js';
import '../Loading/Loading.js';
import '../Modal/Modal.js';
import '../Navbar/Nav.js';
import '../Navbar/Navbar.js';
import '../Navbar/NavDropdown.js';
import '../Pagination/Pagination.js';
import '../Popover/styles.js';
import '../ProgressBar/styles.js';
import '../Radio/RadioGroup.js';
import '../Select/Select.js';
import 'react-dom';
import '../SideNavigation/Accordion/styles.js';
import '../SideNavigation/styles.js';
import '../SideNavigation/Accordion/AccordionRootProvider.js';
import '../SideNavigation/Accordion/AccordionItem.js';
import '../Dropdown/styles.js';
import '../Skeleton/styles.js';
import '../SplashFeedback/styles.js';
import '../Steps/interfaces.js';
import '../Steps/styles.js';
import '../Table/Table.js';
import '../Tabs/context.js';
import '../Tabs/styles.js';
import '../Tabs/Tab.js';
import '../Textarea/Textarea.js';
import '../Textarea/TextareaBody.js';
import '../ThemeProvider/ThemeProvider.js';
import 'styled-components';
import '../Timeline/styles.js';
import '../Toast/styles.js';
import '../Toggle/styles.js';
import '../YouTubeVideoPlayer/styles.js';

const PORTAL_ID = `${PORTAL_PREFIX}-drawer`;
function DrawerComponent({ children, closeDrawer, isOpen, wrapperZIndex, width, }) {
    const isLowerResolution = useIsLowerResolution();
    const [visibility, setVisibility] = useState({
        isContentVisible: isOpen,
        isWrapperVisible: isOpen,
    });
    const transitionRef = useRef(null);
    const nestedTransitionRef = useRef(null);
    const onDrawerClose = (e) => {
        e.stopPropagation();
        setVisibility((currentState) => (Object.assign(Object.assign({}, currentState), { isContentVisible: false })));
        setTimeout(() => {
            setVisibility((currentState) => (Object.assign(Object.assign({}, currentState), { isWrapperVisible: false })));
        }, ANIMATION_DURATION);
    };
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = '';
            };
        }
    }, [isOpen]);
    const resolvedWidth = typeof width === 'number' ? `${width}px` : width !== null && width !== void 0 ? width : `${CONTAINER_WIDTH}px`;
    const transitionStylesDesktop = Object.assign(Object.assign({}, transitionStyles), { entered: Object.assign(Object.assign({}, transitionStyles.entered), { width: resolvedWidth }) });
    return (React.createElement(Portal, { id: PORTAL_ID },
        React.createElement(Transition, { nodeRef: transitionRef, in: visibility.isWrapperVisible, timeout: ANIMATION_DURATION, onExited: () => closeDrawer(), appear: true }, (wrapperState) => (React.createElement(DrawerWrapper, { ref: transitionRef, style: Object.assign({ transitionDuration: `${ANIMATION_DURATION}ms` }, wrapperTransitionStyles[wrapperState]), zIndex: wrapperZIndex, "data-testid": "drawer", onClick: (e) => onDrawerClose(e) }, visibility ? (React.createElement(Transition, { nodeRef: nestedTransitionRef, in: visibility.isContentVisible, timeout: ANIMATION_DURATION, appear: true }, (containerState) => (React.createElement(DrawerContainer, { onClick: (e) => e.stopPropagation(), ref: nestedTransitionRef, style: isLowerResolution
                ? Object.assign({ transitionDuration: `${ANIMATION_DURATION}ms` }, transitionStylesMobile[containerState]) : Object.assign({ transitionDuration: `${ANIMATION_DURATION}ms` }, transitionStylesDesktop[containerState]) },
            React.createElement(DrawerCloseWrapper, null,
                React.createElement(ButtonIcon, { icon: React.createElement(CloseFill, null), variation: "secondary", onClick: (e) => onDrawerClose(e), "data-testid": "drawer-close-button" })),
            children)))) : null)))));
}
function DrawerHeader(_a) {
    var { children } = _a, props = __rest(_a, ["children"]);
    return (React.createElement(DrawerHeaderContainer, Object.assign({ "data-testid": "drawer-header" }, props), children));
}
function DrawerBody(_a) {
    var { children } = _a, props = __rest(_a, ["children"]);
    return (React.createElement(DrawerBodyContainer, Object.assign({ "data-testid": "drawer-body" }, props), children));
}
function Drawer(props) {
    if (!props.isOpen)
        return React.createElement(React.Fragment, null);
    return React.createElement(DrawerComponent, Object.assign({}, props));
}

export { Drawer, DrawerBody, DrawerHeader, PORTAL_ID };
